import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  Button,
  Card,
  DarkHero,
  DarkStatTile,
  type DarkStatChange,
  EmptyState,
  Icon,
  Page,
  PageHeader,
  PlatformIcon,
  Skeleton,
  Spinner,
  StatBlock,
  compactNumber,
  AsOfMarker,
  formatRelativeTime } from "@cntrl/ui";
import { useBandContext, useCapability } from "../../app/bandContext.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { useConnections } from "../../app/hooks/useConnections.js";
import { useMusicHeroPhoto } from "../../app/hooks/useMusicHeroPhoto.js";
import {
  useMusicStats,
  type MusicStreamingBlock,
  type TrackStatView,
} from "../../app/hooks/useMusicStats.js";
import { ReleaseArtwork } from "../releases/ReleaseArtwork.js";
import { RecentReleaseCard } from "../releases/RecentReleaseCard.js";
import { TopPlaylistList } from "../shared/TopPlaylistList.js";
import { playlistHeader } from "../shared/playlistHeader.js";
import { MobileLatestRelease, MobileSpotifyHero, MobileTopTracks } from "./MusicMobile.js";
import mobileStyles from "./MusicMobile.module.scss";
import styles from "./MusicScreen.module.scss";
import { usePhoneLayout } from "../../app/phoneLayout.js";

type MusicQuery = ReturnType<typeof useMusicStats>;
type RadioBlockView = NonNullable<NonNullable<MusicQuery["data"]>["radio"]>;
type SpotifyHeroView = NonNullable<MusicStreamingBlock["spotify"]>;
type HeroDeltaView = NonNullable<SpotifyHeroView["monthlyListenersDelta"]>;

// The EXACT SiriusXM empty-state copy (TASK-021 C-9) — asserted verbatim by the feature test.
const SXM_EMPTY = "No SXM activity tracked yet for this artist.";
// The EXACT Radio empty-state copy (TASK-021 C-8) — one sentence, no description line.
const RADIO_EMPTY =
  "No radio data yet, terrestrial, digital, and SiriusXM performance will show here once tracked.";
/** The reference caps the top-tracks table at ten rows (`tracks.slice(0, 10)`); the server sends every row. */
const TOP_TRACKS_LIMIT = 10;

/** A release date as `MMM D, YYYY` — the full date Ryan's Top-tracks reference shows (ART-201/202). Parsed
 *  as a plain calendar day, never through the local timezone: `new Date("2026-08-29")` west of UTC lands on
 *  the 28th. A value with no day (or a malformed one) falls back to `MMM YYYY`. */
function formatReleaseDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const [y, m, d] = value.split("-");
  const month = Number(m);
  if (!y || !Number.isFinite(month) || month < 1 || month > 12) return null;
  const day = Number(d);
  return Number.isFinite(day) && day >= 1 && day <= 31 ? `${MONTHS[month - 1]} ${day}, ${y}` : `${MONTHS[month - 1]} ${y}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ---- Region 1 · Most recent release --------------------------------------------------------------------

/**
 * The "Most recent release" region.
 *
 * The card itself is `RecentReleaseCard` — the SAME component the dashboard's analytics column renders,
 * over the same `RecentReleaseBlock` the same backend call composes. It used to be a second hand-built
 * copy here, which is how the two surfaces came to disagree about the sub-line's wording, the meta line's
 * formatting and whether "Most recent release" survives a band with no release.
 *
 * It stays a named region with an `h2`, because that is the reference's own outline (`<section
 * class="mt-latest">`) and because a card at page level would put an `h3` straight under the page `h1` —
 * a skipped level that axe's `heading-order` flags. The card takes the level as a prop for exactly this.
 */
function RecentReleaseSection({ q }: { q: MusicQuery }) {
  // The phone gets a slim card — no stats grid, no upcoming pill (Ryan, ART-190).
  const isMobile = usePhoneLayout();
  if (isMobile) return <MobileLatestRelease rr={q.data?.recentRelease ?? null} />;
  return (
    <RecentReleaseCard
      rr={q.data?.recentRelease ?? null}
      headingLevel="h2"
      headingId="music-release-heading"
    />
  );
}

// ---- Region 2 · Streaming ------------------------------------------------------------------------------

type SortKey = "streams28d" | "streamsLifetime";
type SortDir = "desc" | "asc";
type Sort = { key: SortKey; dir: SortDir } | null;

/**
 * One sortable column header.
 *
 * **Module scope is load-bearing.** Declared inside `TopTracksTable`'s body it was a NEW function identity
 * on every render, so React saw a new element `type`, unmounted the `<th>` subtree and mounted a fresh
 * `<button>` — a keyboard user who tabbed to "28D Str." and pressed Enter had focus dumped to `<body>` and
 * had to tab in from the top of the page again, and every background refetch (`sync-completed`, window
 * focus) stole focus the same way mid-interaction. The pre-parity code inlined literal `<button>`s and did
 * not have the bug; hoisting keeps the element type stable so React updates the button in place, preserving
 * focus and `:focus-visible`.
 */
function SortHeader({
  sortKey,
  sort,
  onToggle,
  children,
}: {
  sortKey: SortKey;
  sort: Sort;
  onToggle: (key: SortKey) => void;
  children: string;
}) {
  return (
    <button type="button" className={styles.sortBtn} onClick={() => onToggle(sortKey)}>
      {children}
      {sort?.key === sortKey && <Icon icon={sort.dir === "asc" ? ChevronUp : ChevronDown} size={16} />}
    </button>
  );
}

function TopTracksTable({ tracks }: { tracks: TrackStatView[] }) {
  const [sort, setSort] = useState<Sort>(null);

  const rows = useMemo(() => {
    const ordered = !sort
      ? tracks // server order (28d streams desc, nulls last) is the default
      : [...tracks].sort((a, b) => {
          const dir = sort.dir === "asc" ? 1 : -1;
          const av = a[sort.key];
          const bv = b[sort.key];
          if (av == null && bv == null) return 0;
          if (av == null) return 1; // nulls always last
          if (bv == null) return -1;
          return (av - bv) * dir;
        });
    // The cap is applied AFTER the sort, so it always shows the top 10 of the CHOSEN ordering.
    return ordered.slice(0, TOP_TRACKS_LIMIT);
  }, [tracks, sort]);

  const toggle = (key: SortKey) =>
    setSort((s) => (s?.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));
  const ariaSort = (key: SortKey) => (sort?.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : "none");

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col" className={styles.rank}>
              #
            </th>
            <th scope="col">Track</th>
            {/* Both columns are BACK (2026-08-29), and each was cut for a reason that turned out to be wrong
                in a different way.
                `Released` was cut for want of a DTO field, never for want of the join — which had already
                been built for the cover art (audit D-B3).
                `Pop.` was cut as "unrevivable": Spotify's Feb-2026 migration removed the batch /v1/tracks
                endpoint AND the `popularity` field. True of SPOTIFY, and irrelevant — Songstats relays the
                field, and the legacy app moved to it on 2026-07-18 for exactly this reason. What was really
                dead was our SOURCE, and it kept being called nightly to prove it (audit §22.6). */}
            <th scope="col">Released</th>
            <th scope="col" className={styles.num}>
              Pop.
            </th>
            <th scope="col" className={styles.num} aria-sort={ariaSort("streams28d")}>
              <SortHeader sortKey="streams28d" sort={sort} onToggle={toggle}>
                28D Str.
              </SortHeader>
            </th>
            <th scope="col" className={styles.num} aria-sort={ariaSort("streamsLifetime")}>
              <SortHeader sortKey="streamsLifetime" sort={sort} onToggle={toggle}>
                Lifetime Str.
              </SortHeader>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t, i) => (
            <tr key={t.trackKey ?? i}>
              <td className={styles.rank}>{String(i + 1).padStart(2, "0")}</td>
              <td>
                {/* Cover art beside the title (Ryan's ART-202 facelift), the same `ReleaseArtwork` the
                    dashboard's top-songs list uses. */}
                <span className={styles.trackCell}>
                  <ReleaseArtwork
                    size="chip"
                    className={styles.trackArt}
                    seed={t.title ?? t.trackKey ?? ""}
                    mediaId={t.coverMediaId}
                    imageUrl={t.coverImageUrl}
                  />
                  <span className={styles.trackTitle}>{t.title ?? "Untitled"}</span>
                </span>
              </td>
              {/* A track on no release, or a release with no date, dashes rather than inventing one. */}
              <td className={styles.released}>{formatReleaseDate(t.releasedOn) ?? "—"}</td>
              {/* Enriched for the top ten only — one vendor call per track, capped at the size of this very
                  table (SpotifyTrackPopSource.TopTrackLimit). A track below the cut dashes. The 0–100 index
                  reads as a number beside a mini bar (Ryan's reference). */}
              <td className={styles.num}>
                {t.popularity != null ? (
                  <span className={styles.popCell}>
                    {t.popularity}
                    <span className={styles.popTrack} aria-hidden="true">
                      <span className={styles.popFill} style={{ inlineSize: `${Math.max(0, Math.min(100, t.popularity))}%` }} />
                    </span>
                  </span>
                ) : (
                  "—"
                )}
              </td>
              <td className={styles.num}>{t.streams28d != null ? compactNumber(t.streams28d) : "—"}</td>
              <td className={styles.num}>{t.streamsLifetime != null ? compactNumber(t.streamsLifetime) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The eight hero metrics, in the ORDER the product owner ruled (ART-98) — two rows of four, read across:
 *
 *     Monthly listeners · Followers · 28d streams   · Lifetime streams
 *     Playlist reach    · Playlists · Current editorial playlists · Popularity score
 *
 * The order is the ruling, so it lives in one ordered table rather than in the sequence of eight `if`s that
 * used to build the row — an `if` chain is an order nobody can see, and this one had drifted to
 * listeners/followers/28d/popularity/reach/playlists/lifetime/editorial.
 *
 * `pick` reads the hero DTO; `delta` reads it too, and ALL EIGHT now carry one (PO ruling, 2026-08-29). It
 * used to be three, because those were the three the server computed a percent for — and the bottom row was
 * blank as a result. The metrics were never the problem, the UNIT was: `streams_total`/`playlist_reach_total`
 * are running TOTALS whose history swamps any ratio, and `popularity_current` is a bounded 0-100 index that
 * moves in points. Those come back as `absolute` and render as a flat count (`+2.1m`, `+3`); the levels come
 * back as `percent`. The unit rides the value from the server (`HeroDelta`), so this table never decides how
 * a number should read — and `delta` is still ABSENT on a tile whose pair did not qualify, which stays the
 * ordinary case rather than a failure.
 */
const HERO_TILES: {
  label: string;
  pick: (sp: SpotifyHeroView) => number | null | undefined;
  delta?: (sp: SpotifyHeroView) => DarkStatChange | null | undefined;
  suffix?: string;
  exact?: boolean;
}[] = [
  { label: "Monthly listeners", pick: (sp) => sp.monthlyListeners, delta: (sp) => heroDelta(sp.monthlyListenersDelta) },
  { label: "Followers", pick: (sp) => sp.followers, delta: (sp) => heroDelta(sp.followersDelta) },
  { label: "28d streams", pick: (sp) => sp.streams28d, delta: (sp) => heroDelta(sp.streams28dDelta) },
  { label: "Lifetime streams", pick: (sp) => sp.streamsTotal, delta: (sp) => heroDelta(sp.streamsTotalDelta) },
  { label: "Playlist reach", pick: (sp) => sp.playlistReachTotal, delta: (sp) => heroDelta(sp.playlistReachDelta) },
  { label: "Playlists", pick: (sp) => sp.playlistsTotal, delta: (sp) => heroDelta(sp.playlistsDelta), exact: true },
  { label: "Current editorial playlists", pick: (sp) => sp.playlistsEditorialCurrent, delta: (sp) => heroDelta(sp.playlistsEditorialDelta), exact: true },
  { label: "Popularity score", pick: (sp) => sp.popularity, delta: (sp) => heroDelta(sp.popularityDelta), suffix: "/100", exact: true },
];

/**
 * The wire `HeroDelta` → the tile's change shape. The unit is a string on the wire (an OpenAPI enum would
 * pin the vocabulary in two places), so it is narrowed HERE, once: anything that is not the server's
 * `absolute` reads as a percent, which is the safe default because it is what every level sends and what
 * this component rendered before units existed. A delta with no value is no delta.
 */
function heroDelta(d: HeroDeltaView | null | undefined): DarkStatChange | null {
  if (d?.value == null) return null;
  return { value: d.value, unit: d.unit === "absolute" ? "absolute" : "percent" };
}

/** The dark Spotify hero: a UNIFORM, VARIABLE-LENGTH stat row. A tile is emitted only when the worker
 *  returned a value — missing metrics are never padded with dashes (the reference's hard rule), so the
 *  fixed order above is the order of whatever SURVIVES the filter, never eight reserved slots. */
function SpotifyHeroCard({ sp, photoUrl }: { sp: SpotifyHeroView; photoUrl: string | null }) {
  const tiles = HERO_TILES.flatMap((t) => {
    const value = t.pick(sp);
    return value == null ? [] : [{ ...t, value, delta: t.delta?.(sp) ?? null }];
  });

  return (
    // `className` carries this hero's own HEIGHT (`.spotifyHero` — a floor, with the stats centred in it).
    // The shell owns the surface and the type; how tall the streaming hero stands is this page's call, so it
    // rides the documented escape hatch rather than a variant on a primitive six other surfaces share.
    <DarkHero
      className={styles.spotifyHero}
      photoUrl={photoUrl}
      eyebrow={<SpotifyBrand />}
      action={<SyncSpinner />}
      headGap="tight"
    >
      {tiles.length > 0 && (
        <div className={styles.heroStats}>
          {tiles.map((t) => (
            <DarkStatTile
              key={t.label}
              label={t.label}
              value={t.value}
              suffix={t.suffix}
              exact={t.exact}
              delta={t.delta}
              size="lg"
              // ART-98: no chip fill behind the figures. The delta line is untouched — `StatDelta`'s
              // `inverse` tone was already fill-free, so the only "pill" here was the tile's own.
              chrome="none"
            />
          ))}
        </div>
      )}
    </DarkHero>
  );
}

/**
 * The in-flight sync indicator, in the hero's top-right `action` slot — so it sits inside the hero's own
 * padding rather than being positioned against it.
 *
 * Deliberately WHITE and unlabelled-on-screen: it sits on the dark scrim, and a sync is background bookkeeping
 * a band did not ask for by name. It carries an accessible name for screen readers, and renders NOTHING when
 * idle so the slot costs no layout.
 *
 * Both terminal outcomes clear it without any client-side timeout: `syncing` is derived server-side from
 * dispatched-or-due, and a land parks the schedule while a degrade backs it off — so success and failure both
 * stop the spin. The `sync-completed` realtime signal invalidates this query, so it disappears live rather
 * than on the next poll.
 */
function SyncSpinner() {
  const connections = useConnections();
  if (!connections.data?.freshness?.syncing) return null;

  return <Spinner size={16} className={styles.heroSyncSpinner} label="Syncing your streaming data" />;
}

function SpotifyBrand() {
  return (
    <span className={styles.heroBrand}>
      <PlatformIcon platform="spotify" size={16} />
      Spotify
    </span>
  );
}

/** The reference's `.mt-connect` card: one dark card carrying the brand row, a headline, a blurb and a CTA. */
function ConnectCard({
  title,
  blurb,
  cta,
  onCta,
  showCta,
}: {
  title: string;
  blurb: string;
  cta: string;
  onCta: () => void;
  showCta: boolean;
}) {
  return (
    <DarkHero eyebrow={<SpotifyBrand />}>
      <div className={styles.connect}>
        <h3 className={styles.connectTitle}>{title}</h3>
        <p className={styles.connectBlurb}>{blurb}</p>
        {showCta && (
          // ghost (paper fill), NOT the primary/accent variant — a filled brand button on the spotlight glow
          // fails SC 1.4.11; the white ghost fill gives a legible boundary and focus ring on dark.
          <Button variant="ghost" size="sm" onClick={onCta}>
            {cta}
          </Button>
        )}
      </div>
    </DarkHero>
  );
}

/**
 * The hero / connect slot, chosen **data-first**.
 *
 * A Spotify doc wins outright — if we hold numbers we render them, whatever the connection card says (the
 * `MarketingScreen` precedent: never flash a "connect" CTA at a band that is plainly connected). Only with
 * no doc do we consult `useConnections` to tell *not connected* from *connected, no artist reserved* from
 * *connected, waiting on the first sync* — a distinction the reference could not make.
 *
 * PENDING and FAILED are deliberately different branches. This hook only mounts once the music read has
 * settled, so the connections GET is a SECOND round-trip: treating "not successful" as one state painted the
 * honest-empty degrade during that flight and swapped it for the Connect hero a moment later — the
 * reference's `flash-fix 2026-07-24` defect verbatim ("the honest-empty sections … used to render DURING the
 * first fetch — reading as a disconnected account on every cold visit"). So in flight we hold the slot with a
 * skeleton, and the generic degrade is reserved for a read this seat genuinely cannot have (a roster/org seat
 * may not hold it) — the only case AC8 specifies.
 */
function SpotifyHeroSlot({ sp }: { sp: SpotifyHeroView | null }) {
  const navigate = useNavigate();
  const { orgId } = useBandContext();
  const connections = useConnections();
  const photoUrl = useMusicHeroPhoto();
  const isMobile = usePhoneLayout();
  // An org-lens viewer cannot connect someone else's Spotify — the explanation stays, the button goes.
  const canConnect = orgId == null;
  const goToConnections = () => void navigate({ to: "/settings/connections" });

  if (sp)
    return isMobile ? (
      // The old app's phone hero (Ryan, ART-190): listeners as the headline, lifetime + popularity on the
      // right, a ruled 2x2 under it.
      <MobileSpotifyHero sp={sp} photoUrl={photoUrl} action={<SyncSpinner />} />
    ) : (
      <SpotifyHeroCard sp={sp} photoUrl={photoUrl} />
    );

  // Still in flight — reserve the slot, never guess at the answer. `useBandQuery` is never `enabled: false`,
  // so `isPending` here means genuinely fetching rather than parked.
  // `Skeleton` is `aria-hidden` on purpose: the page already owns the ONE `role="status"` region (B-1), and a
  // second live region announcing a slot would undo that.
  if (connections.isPending) return <Skeleton variant="block" />;

  if (connections.isError) {
    return (
      <Card>
        <EmptyState
          title="No streaming data yet"
          description="Connect Spotify to see your catalog's streaming performance."
        />
      </Card>
    );
  }

  const spotifyConnected = (connections.data.items ?? []).some((c) => c.provider === "spotify" && c.connected === true);
  if (!spotifyConnected) {
    return (
      <ConnectCard
        title="Connect Spotify"
        // The reference's string, RESTORED IN FULL. DV-4 dropped the "editorial placements," clause because
        // we modelled no editorial signal (D-4); `e345663` landed `playlists_editorial_current` for the hero
        // and `playlists_editorial_total` for the release strip, so DV-4's own trigger has fired and the CTA
        // no longer promises anything the platform cannot deliver.
        blurb="Link this artist's Spotify to pull monthly listeners, streams, top cities, editorial placements, and track performance."
        cta="Connect Spotify"
        onCta={goToConnections}
        showCta={canConnect}
      />
    );
  }
  if (!connections.data.spotifyArtist?.artistId) {
    return (
      <ConnectCard
        title="Reserve your Spotify artist"
        blurb="Spotify is connected — pick this artist's profile in Settings → Connections so we can pull streaming analytics."
        cta="Choose artist"
        onCta={goToConnections}
        showCta={canConnect}
      />
    );
  }
  return (
    <Card>
      <EmptyState
        title="No streaming data yet"
        description="Spotify is connected — the first sync lands within a day."
      />
    </Card>
  );
}

function StreamingSection({ q }: { q: MusicQuery }) {
  const isMobile = usePhoneLayout();
  const s = q.data?.streaming;
  const sp = s?.spotify ?? null;
  const cities = s?.topCities ?? [];
  const tracks = s?.tracks ?? [];
  const playlists = sp?.topPlaylists ?? [];
  // `streaming.listenersTrend` is deliberately NOT read any more: ART-98 dropped the hero sparkline, and it
  // fed nothing else on this surface. The field stays on the wire (the roster overview composes its own
  // series the same way) — this screen simply no longer draws a chart from it.
  // Amazon + Apple DSP cards were REMOVED here (Ryan, ART-202) — they already live on the dashboard's Audience
  // rail, so a second copy on this tab was redundant. The "Not this artist" disown control was removed in the
  // same pass; the disown endpoints remain for a future home.

  // Recent adds: the placements' own count and reach (ART-283); the vendor fallback keeps the artist's current reach.
  const playlistSubtitle = playlistHeader(sp) ?? undefined;

  return (
    <section className={styles.section} aria-labelledby="music-streaming-heading">
      {/* No VISIBLE section header (ART-156): the page H1 "Streaming" names this lead region and carries its
          freshness marker, so a second visible "Streaming" would be the duplicate the ticket removes. The
          heading stays as an sr-only h2 so `aria-labelledby` still resolves, the region keeps its name, and
          the outline bridges h1 → h2 → the h3 cards below rather than jumping. */}
      <h2 id="music-streaming-heading" className={styles.srOnly}>
        Streaming
      </h2>

      <SpotifyHeroSlot sp={sp} />

      {/* Most recent release sits DIRECTLY under the hero (2026-09-13), above the audience/tracks cards —
          it is the headline the rest of the streaming detail hangs off, so it reads first. */}
      <RecentReleaseSection q={q} />

      {/* Top cities renders on its own EMPTY STATE rather than vanishing, matching the dashboard's
          `TopCitiesCard` and the roster heartbeat's — which have always done this. A card that disappears
          when its list is empty is indistinguishable from a card nobody built, and that is not academic
          here: `analytics.audience_cities` has never once been populated by a real sync (the
          `/artists/audience` attempt timeout, worker Program.cs), and this surface's silence is why that
          went unnoticed. Gated on `sp` — the hero's own doc — because a band with no Spotify at all gets
          the ConnectCard above, whose blurb already promises "top cities"; a second empty card under it
          would be noise, not information. */}
      {(sp || playlists.length > 0) && (
        <div className={styles.twoCol}>
          {sp && (
            <Card title="Where your audience lives.">
              {cities.length === 0 ? (
                <EmptyState
                  title="No audience data yet"
                  description="Where your audience lives shows up here once your streaming syncs."
                />
              ) : (
                <ol className={styles.cityList}>
                  {cities.map((c, i) => (
                    <li key={`${c.city}-${i}`} className={styles.cityRow}>
                      <span className={styles.cityRank}>{String(i + 1).padStart(2, "0")}</span>
                      <span className={styles.cityName}>
                        {c.city}
                        {c.region ? <span className={styles.cityRegion}>{c.region}</span> : null}
                      </span>
                      <span className={styles.cityValue}>{c.audience != null ? compactNumber(c.audience) : "—"}</span>
                    </li>
                  ))}
                </ol>
              )}
            </Card>
          )}

          {playlists.length > 0 && (
            <Card title="Recent playlist adds" subtitle={playlistSubtitle}>
              <TopPlaylistList playlists={playlists} />
            </Card>
          )}
        </div>
      )}

      {tracks.length > 0 && isMobile && <MobileTopTracks tracks={tracks} />}

      {tracks.length > 0 && !isMobile && (
        // The rows are the vendor's ranking (ART-274) — every song Songstats ranks for the artist, merged
        // across its 30-day and lifetime lists — not only the tracks entered as a release here. Released /
        // Pop. come from the band's own release row for the same song and dash for one with none. The
        // "Your release tracks · sortable" sub-line was removed (Ryan, ART-202).
        <Card title="Top tracks">
          <TopTracksTable tracks={tracks} />
        </Card>
      )}
    </section>
  );
}

// ---- Region 3 · Radio, and Region 4 · SiriusXM ----------------------------------------------------------

/** A country's ISO code rendered as its flag, from the regional-indicator block. Presentation only — the
 *  vendor returns no flag and no country name (audit §17.4), so this derives one from the code we DO have
 *  rather than landing an invented field. A non-ISO value falls through to no flag, never a mangled glyph. */
function flagOf(code: string | null | undefined): string | null {
  if (!code || !/^[A-Za-z]{2}$/.test(code)) return null;
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

/** The terrestrial card's sub-line — assembled from the parts that landed, never with a gap for one that
 *  didn't. The trailing disclosure is load-bearing rather than decorative: `Digital Radio` stations are
 *  bucketed into this table with the AM/FM ones (the legacy scale-back audit's Option A), and this sentence
 *  is what makes that honest instead of a silent mislabel. */
function terrestrialSummary(r: RadioBlockView): string | null {
  const parts: string[] = [];
  if (r.totalSpins != null) parts.push(`${compactNumber(r.totalSpins)} spins`);
  if (r.terrestrialStations != null) parts.push(`${r.terrestrialStations.toLocaleString()} stations`);
  if (r.countries != null) parts.push(`${r.countries} countries`);
  return parts.length > 0 ? `${parts.join(" · ")} · includes internet radio` : null;
}

function RadioSection({ q }: { q: MusicQuery }) {
  // The phone keeps every radio figure (Ryan, ART-190: "we need the Airplay & XM stats at the bottom") but
  // reads them as a compact strip and list rows — the desktop tables scroll sideways at 390px.
  const isMobile = usePhoneLayout();
  const r = q.data?.radio ?? null;
  const countries = r?.topCountries ?? [];
  const stations = r?.topStations ?? [];
  const summary = r?.stationSummary ?? null;
  // The card renders whenever there is a RADIO module at all — an empty station table is a real state with
  // something to say, not an absence. It stays hidden only when nothing radio-shaped landed, which the
  // module-level guard below already handles.
  const showStations = stations.length > 0 || r?.terrestrialStations != null || r?.totalSpins != null;

  // HONEST-EMPTY GUARD. Unchanged in spirit and now for the right reason: the shaper defect that made this a
  // *symptom* guard is fixed (radio really can populate), so an all-null doc now means what it says — no
  // airplay tracked — which is still exactly when the designed empty state belongs.
  const radioEmpty =
    !r ||
    (r.totalSpins == null &&
      r.terrestrialSpins == null &&
      r.terrestrialStations == null &&
      r.countries == null &&
      countries.length === 0 &&
      stations.length === 0 &&
      r.sxm?.plays == null &&
      (r.sxm?.channels?.length ?? 0) === 0);

  return (
    <section className={styles.section} aria-labelledby="music-radio-heading">
      {/* "updated Xh ago" directly UNDER the header (Ryan, ART-202), not pinned top-right. */}
      <PageHeader as="h2" id="music-radio-heading" title="Radio" subtitle={<AsOfMarker asOf={r?.asOf} />} />

      {radioEmpty ? (
        <Card>
          <EmptyState title={RADIO_EMPTY} />
        </Card>
      ) : (
        <>
          {/* WINDOW AND GRAIN. Every figure here is LIFETIME: `/artists/stats?source=radio` answers
              `radio_plays_total` and `radio_stations_total` under the `_total` convention, and the vendor
              publishes no 28-day radio window at either grain (audit §21.3) — so "· 28d" would be an
              invention, not a rounding. The strip is variable-length like the Spotify hero: a tile per real
              value, never a padded dash.
              `Active stations` and `First adds · 7d` — the reference's other two tiles — are deliberately NOT
              here. Both are filters over the station rows, which are RELEASE grain, and a release-scoped
              count sitting beside an artist-lifetime one in the same strip is the grain trap that put a whole
              back catalogue under one drop's heading. They arrive with a catalogue-wide station job (§22.4). */}
          {isMobile ? (
            <MobileRadioKpis r={r} />
          ) : (
          <div className={styles.kpis}>
            {r.totalSpins != null && (
              <div className={styles.kpi}>
                <StatBlock label="Total spins · lifetime" value={r.totalSpins} />
              </div>
            )}
            {r.terrestrialStations != null && (
              <div className={styles.kpi}>
                <StatBlock label="Terrestrial stations" value={r.terrestrialStations} exact />
              </div>
            )}
            {r.countries != null && (
              <div className={styles.kpi}>
                <StatBlock label="Countries" value={r.countries} exact />
              </div>
            )}
          </div>
          )}

          {(countries.length > 0 || showStations) && (
            <div className={styles.twoCol}>
              {countries.length > 0 && (
                <Card title="Terrestrial radio" subtitle={terrestrialSummary(r) ?? undefined}>
                  <ol className={styles.countryList}>
                    {countries.map((c, i) => (
                      <li className={styles.countryRow} key={`${c.code ?? c.name}-${i}`}>
                        <span className={styles.countryRank} aria-hidden="true">
                          {flagOf(c.code ?? c.name) ?? i + 1}
                        </span>
                        <span className={styles.countryBody}>
                          <span className={styles.countryName}>{c.name}</span>
                          {c.stations != null && (
                            <span className={styles.countryMeta}>{c.stations.toLocaleString()} st.</span>
                          )}
                        </span>
                        <span className={styles.countryVal}>
                          {c.spins != null ? compactNumber(c.spins) : "—"}
                        </span>
                      </li>
                    ))}
                  </ol>
                </Card>
              )}

              {showStations && (
                <Card
                  title="Top stations"
                  // SCOPE, stated on the card. These rows are the featured RELEASE's airplay — the vendor
                  // only attaches stations to a track — while the strip above is artist-lifetime. A table
                  // that quietly stood in for the whole catalogue is precisely the defect this names away.
                  subtitle={
                    stations.length === 0
                      ? "Terrestrial airplay · latest release"
                      : r.stationsScope
                        ? `Terrestrial airplay · sorted by spins · ${r.stationsScope}`
                        : "Terrestrial airplay · sorted by spins"
                  }
                >
                  {/* The reference's `Active stations` and `First adds · 7d` KPIs, moved OFF the strip and
                      onto this card — which is the only place they can be honest. Both are filters over these
                      release-scoped rows, so in a strip beside `Total spins · lifetime` they would read as
                      artist figures. Here they sit under the card's own scope line.
                      Legacy computed both from an activity feed capped at 100 rows (~2 days for a busy
                      artist), so its "28-day" count could never see 28 days and its "first adds" counted
                      recent SPINS rather than new stations — its own author wrote that down. These come from
                      per-station first/last-played stamps, so the second one really is new stations. */}
                  {summary && (
                    <p className={styles.stationMeta}>
                      <span>
                        <b>{summary.tracked ?? 0}</b> stations
                      </span>
                      <span>
                        <b>{summary.active28d ?? 0}</b> active · 28d
                      </span>
                      <span>
                        <b>{summary.new7d ?? 0}</b> new · 7d
                      </span>
                    </p>
                  )}
                  {stations.length === 0 ? (
                    /* SAY it, do not vanish. The card used to render only when it had rows, so a band whose
                       newest release has no airplay saw the station card disappear under a strip reading
                       "2,916 stations" — a card that is absent is indistinguishable from one nobody built,
                       which is the exact failure the Top-cities gap taught us. The table is release-scoped
                       and the strip is artist-lifetime, so this is also the one place that distinction can
                       be explained at the moment it looks wrong. */
                    <p className={styles.stationEmpty}>
                      No airplay tracked for {r.stationsScope ?? "the latest release"} yet.
                      {r.terrestrialStations != null && (
                        <>
                          {" "}
                          This band has {r.terrestrialStations.toLocaleString()} stations lifetime — the table
                          covers the latest release only.
                        </>
                      )}
                    </p>
                  ) : isMobile ? (
                    <MobileStationList stations={stations} />
                  ) : (
                  <div className={styles.tableWrap}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th scope="col" className={styles.rankCol}>
                            #
                          </th>
                          <th scope="col">Station</th>
                          <th scope="col">City</th>
                          <th scope="col" className={styles.num}>
                            Spins
                          </th>
                          <th scope="col" className={styles.num}>
                            Last spin
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {stations.map((st, i) => (
                          <tr key={`${st.name}-${i}`}>
                            <td className={styles.rankCol}>{String(i + 1).padStart(2, "0")}</td>
                            <td>
                              <span className={styles.trackTitle}>{st.name}</span>
                              {st.frequency ? <span className={styles.cityRegion}>{st.frequency}</span> : null}
                              {st.isFirstAdd && <span className={styles.newPill}>1st add</span>}
                            </td>
                            <td>
                              {st.city ?? "—"}
                              {st.country ? `, ${st.country}` : ""}
                            </td>
                            <td className={styles.num}>{st.spins != null ? compactNumber(st.spins) : "—"}</td>
                            {/* Rotation lives IN the last-spin cell, as the reference has it: "in rotation"
                                IS the recency answer, so a separate Status column printed the same fact
                                twice and dashed it the rest of the time. */}
                            <td className={styles.num}>
                              {st.inRotation ? (
                                <span className={styles.rotationPill}>in rotation</span>
                              ) : (
                                (formatRelativeTime(st.lastSpin) ?? "—")
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  )}
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** The lifetime radio figures as a 3-up strip on the phone — a tile per real value, never a padded dash. */
function MobileRadioKpis({ r }: { r: RadioBlockView }) {
  const tiles: { label: string; value: number; exact?: boolean }[] = [];
  if (r.totalSpins != null) tiles.push({ label: "Total spins", value: r.totalSpins });
  if (r.terrestrialStations != null) tiles.push({ label: "Stations", value: r.terrestrialStations, exact: true });
  if (r.countries != null) tiles.push({ label: "Countries", value: r.countries, exact: true });
  if (tiles.length === 0) return null;
  return (
    <div className={mobileStyles.radioKpis}>
      {tiles.map((t) => (
        <div key={t.label} className={mobileStyles.radioKpi}>
          <span className={mobileStyles.radioKpiValue}>{t.exact ? t.value.toLocaleString() : compactNumber(t.value)}</span>
          <span className={mobileStyles.radioKpiLabel}>{t.label}</span>
        </div>
      ))}
    </div>
  );
}

/** Top stations as list rows on the phone: rank · station over its city and last spin · spins. */
function MobileStationList({ stations }: { stations: NonNullable<RadioBlockView["topStations"]> }) {
  return (
    <ol className={mobileStyles.rows}>
      {stations.map((st, i) => {
        const city = [st.city, st.country].filter(Boolean).join(", ");
        const recency = st.inRotation ? "in rotation" : formatRelativeTime(st.lastSpin);
        return (
          <li key={`${st.name}-${i}`} className={mobileStyles.row}>
            <span className={mobileStyles.rowRank}>{String(i + 1).padStart(2, "0")}</span>
            <span className={mobileStyles.rowBody}>
              <span className={mobileStyles.rowName}>
                {st.name}
                {st.frequency ? <span className={styles.cityRegion}> {st.frequency}</span> : null}
                {st.isFirstAdd && <span className={styles.newPill}>1st add</span>}
              </span>
              {(city || recency) && (
                <span className={mobileStyles.rowMeta}>{[city, recency].filter(Boolean).join(" · ")}</span>
              )}
            </span>
            <span className={mobileStyles.rowValue}>
              {st.spins != null ? compactNumber(st.spins) : "—"}
              <span className={mobileStyles.rowValueLabel}>spins</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Region 4 · SiriusXM — its own region, not a card nested inside Radio.
 *
 * PROMOTED 2026-08-29, reversing DV-1. That deferral rested on "promoting a block that cannot populate makes
 * the emptiness more prominent", and on our own probe finding no channel roster behind the vendor's
 * `type: SiriusXM` flag. The legacy repo carries a ruling and a probe that say otherwise
 * (`MUSIC_TAB_IA.md:183`, `MUSIC_TAB_AUDIT.md:169`): satellite is a first-class block because labels pay
 * separate promoters for it, and the per-channel data is real — our sample was one artist who barely charts
 * on satellite. Audit §22.
 *
 * The counterpart rule ships with it, from the legacy mobile UAT (M-MUS-10): *"terrestrial-only data must
 * not fabricate an SXM section"* — no plays and no channels renders NOTHING, not a hero full of dashes.
 * The reference's `tracked separately · sxm_plays_total` sub-line is deliberately not ported: a raw storage
 * field name is not user-facing copy.
 */
/** The derived summary heading: plays and, when we have a channel roster, its size. Singular is handled
 *  because "1 active channels" is the kind of seam that reads as a mock. */
function sxmSummary(sxm: NonNullable<RadioBlockView["sxm"]>): string {
  if (sxm.plays == null) return "Satellite airplay";
  const plays = `${compactNumber(sxm.plays)} plays`;
  if (sxm.activeChannels == null) return plays;
  return `${plays} · ${sxm.activeChannels} active channel${sxm.activeChannels === 1 ? "" : "s"}`;
}

function SxmSection({ q }: { q: MusicQuery }) {
  const isMobile = usePhoneLayout();
  const sxm = q.data?.radio?.sxm ?? null;
  const channels = sxm?.channels ?? [];

  // Nothing landed at all ⇒ no region. Distinct from "plays but no channel breakdown", which is a REAL
  // state and renders the hero plus the exact empty line.
  if (sxm == null || (sxm.plays == null && channels.length === 0)) return null;

  const noChannels = channels.length === 0;

  return (
    /* The region is NAMED "SiriusXM" — its heading is a derived summary ("4.5K plays · 3 active channels"),
       which is a fine visible headline and a useless landmark name on its own. Both ids, so the eyebrow
       supplies the identity and the heading the detail. */
    <section className={styles.section} aria-labelledby="music-sxm-eyebrow music-sxm-heading">
      <div className={styles.sxmSub}>
        <span className={styles.sxmEyebrow} id="music-sxm-eyebrow">
          SiriusXM · Satellite
        </span>
        <PageHeader as="h2" id="music-sxm-heading" title={sxmSummary(sxm)} />
      </div>

      <div className={styles.sxmRow}>
        <div className={styles.sxmCard}>
          <span className={styles.sxmEyebrow}>SiriusXM</span>
          {sxm.plays != null && (
            <>
              <span className={styles.sxmBig}>{compactNumber(sxm.plays)}</span>
              <span className={styles.subLabel}>Total plays · lifetime</span>
            </>
          )}
          {/* Variable-length, like every other tile row on this page: a cell per real value. Legacy padded
              these two with "-" and shipped a dash shell under a promoted header. */}
          {(sxm.activeChannels != null || sxm.latestAdd) && (
            <div className={styles.sxmGrid}>
              {sxm.activeChannels != null && (
                <div className={styles.sxmLatest}>
                  <span className={styles.subLabel}>Active channels</span>
                  <span className={styles.sxmLatestVal}>{sxm.activeChannels}</span>
                </div>
              )}
              {sxm.latestAdd && (
                <div className={styles.sxmLatest}>
                  <span className={styles.subLabel}>Latest channel add</span>
                  <span className={styles.sxmLatestVal}>{sxm.latestAdd}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <Card title="Channel breakdown" subtitle="Every SXM channel currently spinning">
          {noChannels ? (
            <p className={styles.sxmEmptyLine}>{SXM_EMPTY}</p>
          ) : isMobile ? (
            <ol className={mobileStyles.rows}>
              {channels.map((ch, i) => (
                <li key={`${ch.name}-${i}`} className={mobileStyles.row}>
                  <span className={mobileStyles.rowBody}>
                    <span className={mobileStyles.rowName}>
                      {ch.name}
                      {ch.isNew && <span className={styles.newPill}>New</span>}
                    </span>
                    <span className={mobileStyles.rowMeta}>Last spin {formatRelativeTime(ch.lastSpin) ?? "—"}</span>
                  </span>
                  <span className={mobileStyles.rowValue}>
                    {ch.plays != null ? compactNumber(ch.plays) : "—"}
                    <span className={mobileStyles.rowValueLabel}>plays</span>
                  </span>
                </li>
              ))}
            </ol>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col">Channel</th>
                    <th scope="col" className={styles.num}>
                      Plays
                    </th>
                    <th scope="col">Last spin</th>
                  </tr>
                </thead>
                <tbody>
                  {channels.map((ch, i) => (
                    <tr key={`${ch.name}-${i}`}>
                      <td>
                        <span className={styles.trackTitle}>{ch.name}</span>
                        {ch.isNew && <span className={styles.newPill}>New</span>}
                      </td>
                      <td className={styles.num}>{ch.plays != null ? compactNumber(ch.plays) : "—"}</td>
                      <td>{formatRelativeTime(ch.lastSpin) ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}

// ---- The composed Music (Streaming) page ----------------------------------------------------------------

/**
 * **No Charts section** (2026-08-26). It rendered a heading over a permanently empty module: nothing sources
 * chart entries — Chartmetric lands UGC into `analytics.social_posts`, not chart positions — so
 * `MusicReadService.BuildCharts()` returns all-null by design and always would until a new source exists.
 * The legacy app never mounted its own `MusicChartsSection` either; the Dev Reference records it as "dead
 * code with live CSS" and leaves mount-or-delete open. We had the inverse — the section with no data — and
 * this settles it the same way. `MusicChartsBlock` stays on the API deliberately: it costs nothing, and the
 * read is where the work resumes if a chart source ever lands.
 *
 * The Music tab (nav label "Streaming") — ONE card plus TWO sections from ONE composed read
 * (`useMusicStats`), with `as_of` staleness markers and the null → `—` contract. Gated on `releases:read`
 * (owner/AV, manager, label, content; NOT booking/touring) — the SAME capability the server enforces on the
 * endpoint, which via the org lens also serves the roster surface (Label/Mgmt). 28D everywhere.
 *
 * Loading and error are PAGE-level, not per-module: one query drove four identical skeletons, four
 * simultaneous `aria-live` regions and four Retry buttons that all refetched the same key. TanStack's
 * `isLoading` is true only with no cached data, so this is also the reference's cold-load `flash-fix` gate.
 *
 * **Both branches need the no-data guard, not just the skeleton.** In v5 a failed *refetch* sets
 * `status: 'error'` while `data` stays populated, so `isError` alone blanked the whole page — release card,
 * Streaming, Radio and Charts — on any background failure. `music-stats` is invalidated live by the
 * `sync-completed` signal and the client refetches on window focus, so that is a routine path, and
 * collapsing four per-box errors into one page-level branch had *enlarged* its blast radius from one module
 * to the screen. Keyed on `q.data == null`, the last good render survives; its `as_of` markers are what say
 * how stale it is.
 */
export function MusicScreen() {
  const canSeeMusic = useCapability("releases:read");
  const q = useMusicStats();

  if (!canSeeMusic) {
    return (
      <Page>
        <EmptyState
          title="Music isn't available for your role"
          description="The Music analytics tab is available to owner/AV, management, label, and content roles."
        />
      </Page>
    );
  }

  return (
    // The page H1 is "Streaming" (ART-156, matching the nav label). The Streaming section's own "Streaming"
    // h2 was removed in the same change so the two no longer collide (audit D-1 resolved), and this H1
    // carries the streaming freshness marker that used to live on that h2.
    <Page
      title="Streaming"
      // "updated Xh ago" sits directly UNDER the header (Ryan, ART-202), not pinned top-right.
      subtitle={<AsOfMarker asOf={q.data?.streaming?.spotify?.asOf ?? q.data?.streaming?.tracksAsOf} />}
    >
      {q.isLoading ? (
        <div className={styles.skeletons} role="status" aria-live="polite" aria-label="Loading">
          <Skeleton variant="tile" />
          <Skeleton variant="line" width="70%" />
          <Skeleton variant="line" width="45%" />
        </div>
      ) : q.isError && q.data == null ? (
        <Card>
          <EmptyState
            title="Couldn't load"
            description={q.error instanceof ApiError ? q.error.message : "Please try again."}
            action={<Button onClick={() => void q.refetch()}>Retry</Button>}
          />
        </Card>
      ) : (
        <div className={styles.regions}>
          {/* The Spotify hero leads, with the most recent release directly beneath it, then the rest of the
              streaming detail — all inside StreamingSection (2026-09-13). */}
          <StreamingSection q={q} />
          <RadioSection q={q} />
          <SxmSection q={q} />
        </div>
      )}
    </Page>
  );
}
